import type { SceneDocument } from '@/lib/scene-document';
import type { MediaAssetDto, ProductVariantSearchResultDto } from '@/apis/backend/generated/models';

import { useTranslation } from 'react-i18next';
import { paths } from '@/routes/paths';
import { useAuth } from '@/lib/store/auth-store';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { createArtboardDoc } from '@/components/canvas-editor/canvas-artboard';
import { MediaPickerModal } from '@/components/email-generation/MediaPickerModal';
import { PhotosTab } from '@/components/canvas-editor/canvas-sidebar/tabs/PhotosTab';
import { ProductPickerModal } from '@/components/email-generation/ProductPickerModal';
import { ScenePropertiesPanel } from '@/components/canvas-editor/ScenePropertiesPanel';
import { CanvasRemixChatPanel } from '@/components/canvas-editor/CanvasRemixChatPanel';
import { ProductsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/ProductsTab';
import { SceneTextTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneTextTab';
import { SceneIconsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneIconsTab';
import { SceneLinksTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneLinksTab';
import { ComponentsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/ComponentsTab';
import { SceneLayersTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneLayersTab';
import { SceneElementsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneElementsTab';
import { CanvasSidebar, type SidebarTab } from '@/components/canvas-editor/canvas-sidebar/CanvasSidebar';
import { SceneBackgroundsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneBackgroundsTab';
import { useCanvasExportControllerRenderHtml } from '@/apis/backend/generated/canvas-export/canvas-export';
import { extractClientSide, SaveComponentDialog } from '@/components/canvas-editor/canvas-sidebar/tabs/SaveComponentDialog';
import { useMediaLibraryControllerUploadAsset, useMediaLibraryControllerRemoveBackgroundFromUrl } from '@/apis/backend/generated/media-library/media-library';
import {
  CanvasKitEditor,
  CanvasKitEditorProvider,
  type CanvasKitEditorHandle,
  type CanvasContextMenuEvent,
} from '@/components/canvas-editor/CanvasKitEditor';
import {
  useCanvasesControllerUpdate,
  useCanvasesControllerRemove,
  useCanvasesControllerFindOne,
  useCanvasesControllerExportCanvas,
} from '@/apis/backend/generated/canvases/canvases';

import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import LayersIcon from '@mui/icons-material/Layers';
import WidgetsIcon from '@mui/icons-material/Widgets';
import CategoryIcon from '@mui/icons-material/Category';
import InterestsIcon from '@mui/icons-material/Interests';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import {
  Box,
  Tab,
  Menu,
  Tabs,
  Stack,
  Button,
  Divider,
  Tooltip,
  Snackbar,
  IconButton,
  Typography,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  MenuItem as MuiMenuItem,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  GroupWork as GroupIcon,
  ContentCopy as CopyIcon,
  HelpOutline as HelpIcon,
  CropFree as CropFreeIcon,
  Download as DownloadIcon,
  ContentPaste as PasteIcon,
  FileCopy as DuplicateIcon,
  Workspaces as UngroupIcon,
  ArrowBack as ArrowBackIcon,
  DataObject as DataObjectIcon,
  SaveAlt as SaveComponentIcon,
  FlipToBack as SendBackwardIcon,
  FlipToFront as BringForwardIcon,
} from '@mui/icons-material';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/c/$clientId/media-library/canvas/$canvasId',
)({
  component: CanvasEditorPage,
});

// A blank standalone canvas is a proper fixed artboard (a real FRAME that owns
// the background), not a loose background rectangle. Email-format canvases come
// from the workspace "add frame" picker; this generic surface defaults to a
// neutral square artboard.
const blankCanvasJson: SceneDocument = createArtboardDoc({
  kind: 'fixed',
  width: 600,
  height: 600,
});

function CanvasEditorPage() {
  const { t } = useTranslation('mediaLibrary');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canvasId } = Route.useParams();
  const { activeOrganization, activeClient } = useOrganization();
  const { isPlatformAdmin } = useAuth();
  const showDevTools = import.meta.env.DEV || isPlatformAdmin;

  const editorRef = useRef<CanvasKitEditorHandle>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerInitialTab, setMediaPickerInitialTab] = useState<'library' | 'generate'>('library');
  const pickImageResolverRef = useRef<((url: string | null) => void) | null>(null);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [saveComponentOpen, setSaveComponentOpen] = useState(false);
  const [saveComponentNodeIds, setSaveComponentNodeIds] = useState<string[]>([]);
  const [saveComponentData, setSaveComponentData] = useState<Record<string, unknown> | null>(null);
  const [contextMenu, setContextMenu] = useState<CanvasContextMenuEvent | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [hasSelection, setHasSelection] = useState(false);
  const [isExportingSelection, setIsExportingSelection] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [rightTab, setRightTab] = useState<'properties' | 'remix'>('properties');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Opt/Alt+8 / +9 (useEditorEvents) jump to the right-panel tabs — Figma's
  // design / prototype panel slots map to Properties / Remix here.
  useEffect(() => {
    const onRightPanel = (e: Event) => {
      const panel = (e as CustomEvent<{ panel: string }>).detail?.panel;
      if (panel === 'properties') setRightTab('properties');
      else if (panel === 'chat') setRightTab('remix');
    };
    window.addEventListener('nexmail:rightPanel', onRightPanel);
    return () => window.removeEventListener('nexmail:rightPanel', onRightPanel);
  }, []);

  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';
  const mediaLibraryPath = paths.dashboard.tenant(orgId, clientId).mediaLibrary;

  const uploadMutation = useMediaLibraryControllerUploadAsset();
  const removeBackgroundMutation = useMediaLibraryControllerRemoveBackgroundFromUrl();
  const renderHtmlMutation = useCanvasExportControllerRenderHtml();

  const { data: canvas, isLoading } = useCanvasesControllerFindOne(
    orgId, clientId, canvasId,
    { query: { enabled: !!orgId && !!clientId && !!canvasId } },
  );

  const { mutate: updateCanvas } = useCanvasesControllerUpdate({
    mutation: {
      onError: () => setSnackbar({ open: true, message: t('canvasCanvasId.failedToSave', 'Failed to save') }),
    },
  });

  const deleteMutation = useCanvasesControllerRemove();

  const handleDeleteCanvas = useCallback(async () => {
    if (!orgId || !clientId || !canvasId) return;
    if (!window.confirm(t('canvasCanvasId.deleteConfirm', 'Are you sure you want to delete this canvas?'))) return;
    try {
      await deleteMutation.mutateAsync({ orgId, clientId, id: canvasId });
      navigate({ to: mediaLibraryPath });
    } catch {
      setSnackbar({ open: true, message: t('canvasCanvasId.failedToDelete', 'Failed to delete canvas') });
    }
  }, [orgId, clientId, canvasId, deleteMutation, navigate, mediaLibraryPath]);

  const handleNameDoubleClick = useCallback(() => {
    setNameValue(canvas?.name || '');
    setEditingName(true);
  }, [canvas?.name]);

  const handleNameBlur = useCallback(() => {
    setEditingName(false);
    if (!orgId || !clientId || !canvasId) return;
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== canvas?.name) {
      updateCanvas({ orgId, clientId, id: canvasId, data: { name: trimmed } });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes('/canvases');
        },
      });
    }
  }, [orgId, clientId, canvasId, nameValue, canvas?.name, updateCanvas, queryClient]);

  const handleNameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
    if (e.key === 'Escape') setEditingName(false);
  }, []);

  const { mutate: exportCanvas, isPending: isExporting } = useCanvasesControllerExportCanvas({
    mutation: {
      onSuccess: (data) => {
        setSnackbar({ open: true, message: t('canvasCanvasId.exportedToMediaLibrary', 'Exported to media library') });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/media/');
          },
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/canvases');
          },
        });
      },
      onError: () => setSnackbar({ open: true, message: t('canvasCanvasId.exportFailed', 'Export failed') }),
    },
  });

  const initialDocument = useMemo(() => {
    if (!canvas) return null;
    if (canvas.canvasJson) return canvas.canvasJson as Record<string, any> as SceneDocument;
    // New canvas with no saved data yet -- use blank template at the canvas dimensions
    return {
      ...blankCanvasJson,
      width: canvas.width,
      height: canvas.height,
      nodes: {
        bg: {
          ...(blankCanvasJson.nodes as any).bg,
          width: canvas.width,
          height: canvas.height,
        },
      },
    } as any as SceneDocument;
  }, [canvas]);

  // ── Autosave (2s debounce) ────────────────────────────────────────
  const handleModified = useCallback(() => {
    if (!orgId || !clientId || !canvasId) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const doc = editorRef.current?.toJSON();
      if (!doc) return;
      updateCanvas({
        orgId, clientId, id: canvasId,
        data: { canvasJson: doc as any, width: doc.width, height: doc.height },
      });
    }, 2000);
  }, [orgId, clientId, canvasId, updateCanvas]);

  // Cleanup timeout on unmount
  useEffect(() => () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    }, []);

  const handleExport = useCallback(() => {
    if (!orgId || !clientId || !canvasId || !editorRef.current) return;
    // Flush any pending save first
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    const doc = editorRef.current.toJSON();
    if (doc) {
      updateCanvas({ orgId, clientId, id: canvasId, data: { canvasJson: doc as any, width: doc.width, height: doc.height } });
    }
    const dataUrl = editorRef.current.toDataURL({ pixelRatio: 2 });
    if (!dataUrl) return;
    exportCanvas({ orgId, clientId, id: canvasId, data: { dataUrl } });
  }, [orgId, clientId, canvasId, updateCanvas, exportCanvas]);

  const handleCopyJson = useCallback(async () => {
    const doc = editorRef.current?.toJSON();
    if (!doc) {
      setSnackbar({ open: true, message: t('canvasCanvasId.noSceneDocument', 'No scene document available') });
      return;
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(doc, null, 2));
      setSnackbar({ open: true, message: t('canvasCanvasId.jsonCopied', 'Scene JSON copied to clipboard') });
    } catch {
      setSnackbar({ open: true, message: t('canvasCanvasId.failedToCopyJson', 'Failed to copy JSON') });
    }
  }, []);

  const handleSelectionChange = useCallback((_nodeId: string | null) => {
    const store = editorRef.current?.getStore();
    if (!store) { setHasSelection(false); return; }
    setHasSelection(store.getState().selectedIds.size > 0);
  }, []);

  // Render the current selection (or the whole canvas when nothing is selected)
  // to raw 1:1 HTML via the exporter endpoint.
  const renderSelectionHtml = useCallback(async (): Promise<string | null> => {
    if (!orgId || !clientId || !editorRef.current) return null;
    const sceneDocument = editorRef.current.toSelectionDoc() ?? editorRef.current.toJSON();
    if (!sceneDocument) return null;
    const { html } = await renderHtmlMutation.mutateAsync({
      orgId, clientId,
      data: { sceneDocument: sceneDocument as any },
    });
    return html;
  }, [orgId, clientId, renderHtmlMutation]);

  const handleCopyHtml = useCallback(async () => {
    setExportMenuAnchor(null);
    setIsExportingSelection(true);
    try {
      const html = await renderSelectionHtml();
      if (html == null) return;
      await navigator.clipboard.writeText(html);
      setSnackbar({ open: true, message: t('canvasCanvasId.htmlCopied', 'HTML copied to clipboard') });
    } catch {
      setSnackbar({ open: true, message: t('canvasCanvasId.exportHtmlFailed', 'Export HTML failed') });
    } finally {
      setIsExportingSelection(false);
    }
  }, [renderSelectionHtml]);

  const handleDownloadHtml = useCallback(async () => {
    setExportMenuAnchor(null);
    setIsExportingSelection(true);
    try {
      const html = await renderSelectionHtml();
      if (html == null) return;
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'selection.html';
      a.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: t('canvasCanvasId.downloadedHtml', 'Downloaded selection.html') });
    } catch {
      setSnackbar({ open: true, message: t('canvasCanvasId.exportHtmlFailed', 'Export HTML failed') });
    } finally {
      setIsExportingSelection(false);
    }
  }, [renderSelectionHtml]);

  const handleRemoveBackground = useCallback(async (imageSrc: string): Promise<string | null> => {
    if (!orgId || !clientId) return null;
    try {
      const result = await removeBackgroundMutation.mutateAsync({
        orgId, clientId,
        data: { imageUrl: imageSrc },
      });
      return result.publicUrl ?? null;
    } catch {
      return null;
    }
  }, [orgId, clientId, removeBackgroundMutation]);

  const handlePickImage = useCallback(() => new Promise<string | null>((resolve) => {
      pickImageResolverRef.current = resolve;
      setMediaPickerInitialTab('library');
      setMediaPickerOpen(true);
    }), []);

  const handleOpenAIGenerate = useCallback(() => {
    setMediaPickerInitialTab('generate');
    setMediaPickerOpen(true);
  }, []);

  const handleMediaSelect = useCallback((assets: MediaAssetDto[]) => {
    const url = assets[0]?.publicUrl;
    if (!url) return;
    if (pickImageResolverRef.current) {
      pickImageResolverRef.current(url);
      pickImageResolverRef.current = null;
    } else {
      editorRef.current?.addImage(url);
    }
  }, []);

  const handleProductSelect = useCallback((products: ProductVariantSearchResultDto[]) => {
    const imageUrl = products[0]?.productImages?.[0]?.src;
    if (imageUrl) editorRef.current?.addImage(imageUrl);
  }, []);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !orgId || !clientId) return;
    e.target.value = '';
    try {
      const result = await uploadMutation.mutateAsync({
        orgId, clientId,
        data: { file, category: 'custom' },
      });
      const url = (result as any).publicUrl || (result as any).data?.publicUrl;
      if (url) editorRef.current?.addImage(url);
    } catch { /* ignore */ }
  }, [orgId, clientId, uploadMutation]);

  // OS file drop onto the canvas: upload each image, then place it where it
  // was dropped (filling the shape under the cursor, or as a new photo node).
  const handleOsFilesDropped = useCallback(async (
    files: File[],
    placement: { targetNodeId?: string; atScene?: { x: number; y: number } },
  ) => {
    if (!orgId || !clientId) return;
    for (const file of files) {
      try {
        const result = await uploadMutation.mutateAsync({
          orgId, clientId,
          data: { file, category: 'custom' },
        });
        const url = (result as any).publicUrl || (result as any).data?.publicUrl;
        if (url) editorRef.current?.placeImage(url, placement);
        // Only the first file takes the drop target; the rest land as photo
        // nodes at the drop point so they don't overwrite each other's fill.
        placement = { atScene: placement.atScene };
      } catch {
        setSnackbar({ open: true, message: t('canvasCanvasId.uploadFailed', 'Failed to upload image') });
      }
    }
  }, [orgId, clientId, uploadMutation, t]);

  // ── Context menu ────────────────────────────────────────────────────
  const handleContextMenu = useCallback((event: CanvasContextMenuEvent) => {
    setContextMenu(event);
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const handleContextMenuAction = useCallback((action: string) => {
    const editor = editorRef.current;
    if (!editor) { closeContextMenu(); return; }

    switch (action) {
      case 'copy': editor.getStore().getState().copySelected(); break;
      case 'paste': editor.getStore().getState().pasteClipboard(); break;
      case 'duplicate': editor.getStore().getState().duplicateSelected(); break;
      case 'delete': editor.deleteSelected(); break;
      case 'group': editor.groupSelected(); break;
      case 'ungroup': editor.ungroupSelected(); break;
      case 'bringForward': editor.bringForward(); break;
      case 'sendBackward': editor.sendBackward(); break;
      case 'bringToFront': editor.bringToFront(); break;
      case 'sendToBack': editor.sendToBack(); break;
      case 'saveComponent': {
        const store = editor.getStore();
        const s = store.getState();
        const ids = [...s.selectedIds];
        if (ids.length > 0) {
          const extracted = extractClientSide(s.doc, ids);
          setSaveComponentNodeIds(ids);
          setSaveComponentData(extracted.componentData);
          setSaveComponentOpen(true);
        }
        break;
      }
      default:
        break;
    }
    closeContextMenu();
  }, [closeContextMenu]);

  // ── Sidebar tabs ────────────────────────────────────────────────────
  const sidebarTabs: SidebarTab[] = useMemo(() => [
    // Layers leads so Opt/Alt+1 = Layers in every canvas host (Figma's
    // panel numbering; the email builder already lists it first).
    {
      id: 'layers',
      label: t('canvasCanvasId.sidebar.layers', 'Layers'),
      icon: LayersIcon,
      content: (
        <SceneLayersTab
          onSaveAsComponent={(nodeIds) => {
            const store = editorRef.current?.getStore();
            if (!store) return;
            const doc = store.getState().doc;
            const extracted = extractClientSide(doc, nodeIds);
            setSaveComponentNodeIds(nodeIds);
            setSaveComponentData(extracted.componentData);
            setSaveComponentOpen(true);
          }}
        />
      ),
    },
    {
      id: 'text',
      label: t('canvasCanvasId.sidebar.text', 'Text'),
      icon: TextFieldsIcon,
      content: <SceneTextTab editorRef={editorRef} />,
    },
    {
      id: 'elements',
      label: t('canvasCanvasId.sidebar.elements', 'Elements'),
      icon: CategoryIcon,
      content: <SceneElementsTab editorRef={editorRef} />,
    },
    {
      id: 'icons',
      label: t('canvasCanvasId.sidebar.icons', 'Icons'),
      icon: InterestsIcon,
      content: <SceneIconsTab />,
    },
    {
      id: 'backgrounds',
      label: t('canvasCanvasId.sidebar.backgrounds', 'BGs'),
      icon: WallpaperIcon,
      content: <SceneBackgroundsTab editorRef={editorRef} />,
    },
    ...(orgId && clientId ? [{
      id: 'components',
      label: t('canvasCanvasId.sidebar.library', 'Library'),
      icon: WidgetsIcon,
      content: (
        <ComponentsTab
          orgId={orgId}
          clientId={clientId}
          editorRef={editorRef}
        />
      ),
    }] : []),
    {
      id: 'media',
      label: t('canvasCanvasId.sidebar.media', 'Media'),
      icon: ImageIcon,
      content: (
        <PhotosTab
          orgId={orgId}
          clientId={clientId}
          onImageClick={(url) => editorRef.current?.addImage(url)}
          onUploadFromComputer={() => fileInputRef.current?.click()}
          onOpenAIImageGen={handleOpenAIGenerate}
        />
      ),
    },
    ...(clientId ? [{
      id: 'products',
      label: t('canvasCanvasId.sidebar.products', 'Products'),
      icon: ShoppingBagIcon,
      content: (
        <ProductsTab
          clientId={clientId}
          onProductSelect={(product: ProductVariantSearchResultDto) => {
            const imageUrl = product.productImages?.[0]?.src;
            if (imageUrl) editorRef.current?.addImage(imageUrl);
          }}
        />
      ),
    }] : []),
    {
      id: 'links',
      label: t('canvasCanvasId.sidebar.links', 'Links'),
      icon: LinkIcon,
      content: <SceneLinksTab />,
    },
  ] as SidebarTab[], [orgId, clientId, handleOpenAIGenerate, t]);

  // ── Loading / Error states ──────────────────────────────────────────
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canvas || !initialDocument) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
        <Typography variant="h6" color="text.secondary">{t('canvasCanvasId.notFound', 'Canvas not found')}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: mediaLibraryPath })}>
          {t('canvasCanvasId.backToMediaLibrary', 'Back to Media Library')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          height: 56, px: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={() => navigate({ to: mediaLibraryPath })}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          {editingName ? (
            <input
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              autoFocus
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                fontFamily: 'inherit',
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.23)',
                borderRadius: 4,
                padding: '2px 8px',
                maxWidth: 300,
                outline: 'none',
              }}
            />
          ) : (
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              sx={{ maxWidth: 300, cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
              onDoubleClick={handleNameDoubleClick}
            >
              {canvas.name || t('canvasCanvasId.untitled', 'Untitled')}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {canvas.width}x{canvas.height}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip
            title={
              <Box sx={{ fontSize: '12px', lineHeight: 1.6 }}>
                <strong>{t('canvasCanvasId.shortcuts.title', 'Canvas shortcuts')}</strong><br />
                {t('canvasCanvasId.shortcuts.zoom', 'Ctrl/Cmd + Scroll -- Zoom in/out')}<br />
                {t('canvasCanvasId.shortcuts.scrollPan', 'Scroll -- Pan')}<br />
                {t('canvasCanvasId.shortcuts.spaceDrag', 'Space + Drag -- Pan')}<br />
                {t('canvasCanvasId.shortcuts.click', 'Click -- Select object')}<br />
                {t('canvasCanvasId.shortcuts.shiftClick', 'Shift + Click -- Multi-select')}<br />
                {t('canvasCanvasId.shortcuts.doubleClick', 'Double-click -- Enter group')}<br />
                {t('canvasCanvasId.shortcuts.deleteKey', 'Delete/Backspace -- Delete selected')}<br />
                {t('canvasCanvasId.shortcuts.undo', 'Ctrl/Cmd + Z -- Undo')}<br />
                {t('canvasCanvasId.shortcuts.redo', 'Ctrl/Cmd + Shift + Z -- Redo')}<br />
                {t('canvasCanvasId.shortcuts.copyPaste', 'Ctrl/Cmd + C/V -- Copy/Paste')}<br />
                {t('canvasCanvasId.shortcuts.duplicate', 'Ctrl/Cmd + D -- Duplicate')}<br />
                {t('canvasCanvasId.shortcuts.group', 'Ctrl/Cmd + G -- Group')}<br />
                {t('canvasCanvasId.shortcuts.ungroup', 'Ctrl/Cmd + Shift + G -- Ungroup')}
              </Box>
            }
            arrow
            placement="bottom-end"
          >
            <IconButton size="small" sx={{ color: 'text.secondary' }}>
              <HelpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {showDevTools && (
            <Tooltip title={t('canvasCanvasId.copyJsonTooltip', 'Copy scene document JSON to clipboard')}>
              <Button
                variant="outlined" size="small"
                startIcon={<DataObjectIcon />}
                onClick={handleCopyJson}
                sx={{ textTransform: 'none' }}
              >
                {t('canvasCanvasId.copyJson', 'Copy JSON')}
              </Button>
            </Tooltip>
          )}
          <Button
            variant="outlined" size="small" color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteCanvas}
            sx={{ textTransform: 'none' }}
          >
            {t('common:delete')}
          </Button>
          {hasSelection && (
            <>
              <Button
                variant="outlined" size="small"
                startIcon={isExportingSelection ? <CircularProgress size={14} /> : <CropFreeIcon />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                disabled={isExportingSelection}
                sx={{ textTransform: 'none' }}
              >
                {t('canvasCanvasId.exportHtml', 'Export HTML')}
              </Button>
              <Menu
                anchorEl={exportMenuAnchor}
                open={!!exportMenuAnchor}
                onClose={() => setExportMenuAnchor(null)}
              >
                <MuiMenuItem onClick={handleCopyHtml}>{t('common:copyToClipboard')}</MuiMenuItem>
                <MuiMenuItem onClick={handleDownloadHtml}>{t('canvasCanvasId.downloadHtml', 'Download .html')}</MuiMenuItem>
              </Menu>
            </>
          )}
          <Button
            variant="contained" size="small"
            startIcon={isExporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
            onClick={handleExport} disabled={isExporting}
            sx={{ textTransform: 'none' }}
          >
            {t('canvasCanvasId.exportToMediaLibrary', 'Export to Media Library')}
          </Button>
        </Stack>
      </Box>

      {/* Editor Body */}
      <CanvasKitEditorProvider initialDocument={initialDocument}>
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <CanvasSidebar tabs={sidebarTabs} initialTab="layers" />

          <CanvasKitEditor
            ref={editorRef}
            initialDocument={initialDocument}
            onDocumentChange={handleModified}
            onSelectionChange={handleSelectionChange}
            onContextMenu={handleContextMenu}
            onOsFilesDropped={handleOsFilesDropped}
          />

          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Tabs
              value={rightTab}
              onChange={(_, v) => setRightTab(v)}
              variant="fullWidth"
              sx={{
                minHeight: 36,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: 36,
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  py: 0,
                },
              }}
            >
              <Tab value="properties" label={t('canvasCanvasId.rightPanel.properties', 'Properties')} />
              <Tab value="remix" label={t('canvasCanvasId.rightPanel.remix', 'Remix')} />
            </Tabs>
            {/* Both panels stay mounted — toggling display preserves chat history
             *  and any in-flight remix stream when the user switches tabs. */}
            <Box sx={{ flex: 1, overflow: 'auto', display: rightTab === 'properties' ? 'block' : 'none' }}>
              <ScenePropertiesPanel
                onPickImage={handlePickImage}
                onRemoveBackground={handleRemoveBackground}
              />
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden', display: rightTab === 'remix' ? 'block' : 'none' }}>
              <CanvasRemixChatPanel canvasId={canvasId} editorRef={editorRef} />
            </Box>
          </Box>
        </Box>
      </CanvasKitEditorProvider>

      {/* Right-click context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
        slotProps={{ paper: { sx: { minWidth: 180, '& .MuiMenuItem-root': { fontSize: '13px', py: 0.75 } } } }}
      >
        {contextMenu && contextMenu.selectedIds.length > 0 ? [
          <MuiMenuItem key="copy" onClick={() => handleContextMenuAction('copy')}>
            <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('canvasCanvasId.contextMenu.copy', 'Copy')}</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Ctrl+C</Typography>
          </MuiMenuItem>,
          <MuiMenuItem key="duplicate" onClick={() => handleContextMenuAction('duplicate')}>
            <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('canvasCanvasId.contextMenu.duplicate', 'Duplicate')}</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Ctrl+D</Typography>
          </MuiMenuItem>,
          <MuiMenuItem key="delete" onClick={() => handleContextMenuAction('delete')}>
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('common:delete')}</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Del</Typography>
          </MuiMenuItem>,
          <Divider key="d1" />,
          <MuiMenuItem key="bringForward" onClick={() => handleContextMenuAction('bringForward')}>
            <ListItemIcon><BringForwardIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('canvasCanvasId.contextMenu.bringForward', 'Bring Forward')}</ListItemText>
          </MuiMenuItem>,
          <MuiMenuItem key="sendBackward" onClick={() => handleContextMenuAction('sendBackward')}>
            <ListItemIcon><SendBackwardIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('canvasCanvasId.contextMenu.sendBackward', 'Send Backward')}</ListItemText>
          </MuiMenuItem>,
          <Divider key="d2" />,
          ...(contextMenu.hasGroup ? [
            <MuiMenuItem key="group" onClick={() => handleContextMenuAction('group')}>
              <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('canvasCanvasId.contextMenu.group', 'Group')}</ListItemText>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Ctrl+G</Typography>
            </MuiMenuItem>,
          ] : []),
          ...(contextMenu.canUngroup ? [
            <MuiMenuItem key="ungroup" onClick={() => handleContextMenuAction('ungroup')}>
              <ListItemIcon><UngroupIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('canvasCanvasId.contextMenu.ungroup', 'Ungroup')}</ListItemText>
            </MuiMenuItem>,
          ] : []),
          ...(orgId && clientId ? [
            <MuiMenuItem key="saveComponent" onClick={() => handleContextMenuAction('saveComponent')}>
              <ListItemIcon><SaveComponentIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('canvasCanvasId.contextMenu.saveAsComponent', 'Save as Component')}</ListItemText>
            </MuiMenuItem>,
          ] : []),
        ] : [
          <MuiMenuItem key="paste" onClick={() => handleContextMenuAction('paste')}>
            <ListItemIcon><PasteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('canvasCanvasId.contextMenu.paste', 'Paste')}</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Ctrl+V</Typography>
          </MuiMenuItem>,
        ]}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      <MediaPickerModal
        open={mediaPickerOpen}
        onClose={() => {
          setMediaPickerOpen(false);
          if (pickImageResolverRef.current) {
            pickImageResolverRef.current(null);
            pickImageResolverRef.current = null;
          }
        }}
        onSelect={handleMediaSelect}
        selectedAssets={[]}
        initialTab={mediaPickerInitialTab}
      />
      <ProductPickerModal
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onSelect={handleProductSelect}
        selectedProducts={[]}
        singleSelect
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      {orgId && clientId && (
        <SaveComponentDialog
          open={saveComponentOpen}
          onClose={() => setSaveComponentOpen(false)}
          orgId={orgId}
          clientId={clientId}
          selectedNodeIds={saveComponentNodeIds}
          inlineComponentData={saveComponentData ?? undefined}
        />
      )}
    </Box>
  );
}
